// ./src/ActionSheet/ActionSheet.tsx
//
// A phone's menu: a bottom sheet (the responsive AppDrawer, hugging its content) of big, full-width rows — the iOS
// action sheet / Material bottom-sheet menu. A Mantine Menu's rows are ~36px and sit under the thumb in a floating box,
// so on a phone they were hard to hit and a mis-tap opened the wrong thing (owner, 2026-10-09: the dashboard's Edit /
// Upgrade / ⋯ menus, then every other menu in the app and on the public site).
//
// ⓘ A MENU, not a dialog: it is opened by a tap and gone after one, so it is not a URL stage (`a-dialog-is-a-drawer`
//   is about drawers that hold work). `ResponsiveMenu` puts it behind a button: a Mantine Menu on a desktop, this sheet
//   on a phone. On the public event site it draws on the event's theme (`PickerSurfaceProvider`, as the pickers do).

import React from 'react';
import { Button, Divider, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppDrawer } from '../AppDrawer';
import { AppIcon } from '../AppIcon';
import { usePickerSurface } from '../form/OptionPickerModal';

export interface ActionSheetItem {
  label: string;
  /** Tabler icon name. */
  iconName?: string;
  /** Anything else drawn before the label (a colour dot, a flag) — instead of `iconName`. */
  leftSection?: React.ReactNode;
  /** e.g. "red" for a destructive action. */
  color?: string;
  disabled?: boolean;
  /** The current choice in a menu of choices — drawn with a check. */
  checked?: boolean;
  /** A line under the label (a command's meaning). */
  description?: string;
  /** The label is code (a slash command, a snippet) — drawn in monospace. */
  mono?: boolean;
  onClick: () => void;
}

export interface ActionSheetSection {
  /** A heading over the section's rows (a Menu.Label on a desktop). */
  label?: string;
  items: ActionSheetItem[];
}

export interface ActionSheetProps {
  opened: boolean;
  onClose: () => void;
  /** The sheet's title (default "Actions"). */
  title?: React.ReactNode;
  sections?: ActionSheetSection[];
  /** Content that is not a list of rows (a reaction palette) — drawn under the sections. */
  children?: React.ReactNode;
}

/**
 * A row's colours. ⚠ A plain row inks with the PAGE's text colour and hovers with its default hover, not a fixed grey:
 * on a dark event theme Mantine's neutral grey was mid-grey on near-black (#138 review). A coloured row (red, orange)
 * keeps its colour. The chosen row is tinted with the translucent primary, as the full-screen picker's rows are. A
 * disabled row is dimmed, not painted Mantine's solid disabled grey (six grey slabs in the table menu outside a table).
 */
const rowStyle = (item: ActionSheetItem): React.CSSProperties =>
  ({
    ...(item.color ? {} : { '--button-color': 'var(--mantine-color-text)', '--button-hover': 'var(--mantine-color-default-hover)' }),
    ...(item.checked ? { '--button-bg': 'var(--mantine-primary-color-light)' } : {}),
    ...(item.disabled ? { background: 'transparent', color: 'var(--mantine-color-text)', opacity: 0.45 } : {}),
  }) as React.CSSProperties;

export const ActionSheet: React.FC<ActionSheetProps> = ({ opened, onClose, title, sections = [], children }) => {
  const { t } = useTranslation(['common']);
  const surface = usePickerSurface();
  const shown = sections.filter((s) => s.items.length > 0);

  return (
    // ⓘ No focus return: the sheet closes before its item runs, and focus handed back ~10ms later took it from the field
    //   a drawer the item opened had just focused (#138 review).
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title ?? t('common:actions.title', { defaultValue: 'Actions' })}
      actionType="custom"
      mobileFitContent
      returnFocus={false}
      contentStyle={surface}
    >
      <Stack gap={4} pt="xs">
        {shown.map((section, si) => (
          <React.Fragment key={section.label ?? `section-${si}`}>
            {si > 0 && <Divider my={4} />}
            {/* A heading that only repeats the sheet's title is left out. */}
            {section.label && section.label !== title && (
              <Text size="md" fw={600} c="dimmed" px="md" pt={4}>
                {section.label}
              </Text>
            )}
            {section.items.map((item, i) => (
              <Button
                key={`${item.label}-${i}`}
                variant="subtle"
                color={item.color}
                size="lg"
                justify="flex-start"
                fullWidth
                disabled={item.disabled}
                leftSection={item.leftSection ?? (item.iconName ? <AppIcon iconName={item.iconName} size={20} /> : undefined)}
                rightSection={item.checked ? <AppIcon iconName="Check" size={20} /> : undefined}
                aria-pressed={item.checked}
                // Every row grows to its text: a long label wraps instead of being cut off mid-phrase.
                h="auto"
                mih={50}
                py="xs"
                style={rowStyle(item)}
                styles={{ inner: { justifyContent: 'flex-start' }, label: { flex: 1, whiteSpace: 'normal', textAlign: 'left' } }}
                // ⚠ Close FIRST: an item that opens a drawer or navigates must not have this sheet still on top of it.
                onClick={() => {
                  onClose();
                  item.onClick();
                }}
              >
                {item.description ? (
                  <Stack gap={2}>
                    <Text size="md" fw={600} ff={item.mono ? 'monospace' : undefined}>
                      {item.label}
                    </Text>
                    <Text size="md" c="dimmed" fw={400}>
                      {item.description}
                    </Text>
                  </Stack>
                ) : (
                  <Text span inherit ff={item.mono ? 'monospace' : undefined}>
                    {item.label}
                  </Text>
                )}
              </Button>
            ))}
          </React.Fragment>
        ))}
        {children && <Group px="xs">{children}</Group>}
      </Stack>
    </AppDrawer>
  );
};
